import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSData, Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import didUpdate from 'webapp/modifiers/did-update';

import DataSelect from 'webapp/models-custom/data-select';

import type { RSjqXHR, RSPromise } from 'webapp/services/request';

export interface Args {
	model: DataSelect;
	onLoaded?: () => void;
}

export default class ScorecardPreviewLineComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;

	@tracked previewPromise: RSPromise<unknown> | null = null;
	@tracked previewData: DSData.RootTop | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.schedulePreviewUpdate();
	}

	get sourceData() {
		const source = this.args.model.metricSetup.source;
		return {
			avatar: source?.realAvatar,
			title: source?.title,
		};
	}

	get metricData() {
		return {
			title: this.point?.titleLong || this.args.model.metricSetup.selectedMetricTitle,
		};
	}

	get isPreviewLoading() {
		return !!this.previewPromise;
	}

	get valueStr() {
		return this.isPreviewLoading ? '' : this.point?.valueStr || '-';
	}

	@cached
	get point() {
		const points = this.mmDataSelectsService.getPointsFromData(
			this.args.model,
			this.previewData,
		);
		return points[0];
	}

	get comparePoint() {
		return this.point?.comparePoints[0];
	}

	updatePreviewData() {
		if (!isAlive(this)) {
			return;
		}

		// if request already in progress, cancel it
		if (this.previewPromise) {
			(this.previewPromise.jqPromise as RSjqXHR)?.abort();
		}

		const model = this.args.model;
		if (!model.isSingleOutput || !model.validate()) {
			return;
		}

		const promise = this.mmDataSelectsService.fetchData(model);
		this.previewPromise = promise;

		promise
			.then((reply) => {
				if (promise === this.previewPromise) {
					this.previewData = reply;
				}
			})
			.catch(() => {
				if (promise === this.previewPromise) {
					this.previewData = null;
				}
			})
			.finally(() => {
				if (promise === this.previewPromise) {
					this.previewPromise = null;
				}
				this.args.onLoaded?.();
			});
	}

	@action
	schedulePreviewUpdate() {
		debounce(this, this.updatePreviewData, 100);
	}
	<template>
		<tr
			class={{if this.isPreviewLoading "loading"}}
			{{didUpdate
				this.schedulePreviewUpdate
				@model.metricSetupSpaceAccessId
				@model.metricSetupKey
				@model.metricSetupDimensions
				@model.metricSetupFilters
			}}
		>
			<td width="50%">
				<div class="source">
					<div class="source__icon">
						<img src={{this.sourceData.avatar}} alt />
					</div>
					<div class="source__info">
						<strong>{{this.metricData.title}}</strong>
						<span>{{this.sourceData.title}}</span>
					</div>
				</div>
			</td>
			<td width="50%">
				<div class="value">{{this.valueStr}}</div>
				<div class="change">
					{{#unless this.isPreviewLoading}}
						<span
							class="indicator {{this.comparePoint.indicatorClass}}"
						>{{this.comparePoint.changeStr}}
						</span>
						{{#if this.comparePoint.valueStr}}
							<span class="compare">({{this.comparePoint.valueStr}})</span>
						{{/if}}
					{{/unless}}
				</div>
			</td>
		</tr>
	</template>
}
